import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { ApiError, api } from "../../lib/api";
import type { WatchItemsResponse } from "../../lib/api";
import styles from "./WatchInstructionsEditor.module.css";

type WatchScope = { kind: "camera" | "store"; id: string };

type WatchInstructionsEditorProps = {
  scope: WatchScope;
  /** How often to poll while a request is being reviewed. */
  pollIntervalMs?: number;
};

const DEFAULT_POLL_MS = 1500;

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

export const WatchInstructionsEditor = ({
  scope,
  pollIntervalMs = DEFAULT_POLL_MS,
}: WatchInstructionsEditorProps) => {
  const [raw, setRaw] = useState("");
  const [data, setData] = useState<WatchItemsResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localDeleting, setLocalDeleting] = useState<Set<number>>(new Set());
  const [awaitingOutcome, setAwaitingOutcome] = useState(false);
  const [duplicateOfId, setDuplicateOfId] = useState<number | null>(null);
  // Several editors share a page (the wizard's describe step: the store plus one per
  // camera), so the input id carries the scope.
  const inputId = `watch-instruction-input-${scope.kind}-${scope.id}`;

  const refresh = useCallback(async (): Promise<WatchItemsResponse | null> => {
    try {
      const next = await api.getWatchItems(scope.kind, scope.id);
      setData(next);
      setError(null);
      return next;
    } catch (e) {
      setError(errorText(e, "Couldn't load watch instructions."));
      return null;
    }
  }, [scope.kind, scope.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // While anything is under review (adds or deletes), poll until the worker lands it.
  const reviewing = (data?.reviewing ?? 0) > 0;
  const deleting = (data?.pending_delete_ids?.length ?? 0) > 0 || localDeleting.size > 0;
  useEffect(() => {
    if (!reviewing && !deleting) return;
    const timer = setInterval(() => void refresh(), pollIntervalMs);
    return () => clearInterval(timer);
  }, [reviewing, deleting, refresh, pollIntervalMs]);

  // Resolve the outcome of the add we initiated: a trusted duplicate match highlights
  // the existing instruction instead of adding one (ADR 0022 amendment).
  useEffect(() => {
    if (!awaitingOutcome || data === null || data.reviewing > 0) return;
    setAwaitingOutcome(false);
    if (data.last_outcome?.outcome === "duplicate" && data.last_outcome.item_id !== null) {
      setDuplicateOfId(data.last_outcome.item_id);
      const timer = setTimeout(() => setDuplicateOfId(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [awaitingOutcome, data]);

  // Drop local ghosts once the server no longer knows the item or its delete request.
  useEffect(() => {
    if (data === null || localDeleting.size === 0) return;
    const known = new Set(data.items.map((i) => i.id));
    const next = new Set([...localDeleting].filter((id) => known.has(id)));
    if (next.size !== localDeleting.size) setLocalDeleting(next);
  }, [data, localDeleting]);

  const handleAdd = useCallback(async (): Promise<void> => {
    const text = raw.trim();
    if (!text) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.submitWatchInstruction(scope.kind, scope.id, text);
      setRaw("");
      setAwaitingOutcome(true);
      await refresh();
    } catch (e) {
      setError(errorText(e, "Couldn't save the instruction."));
    } finally {
      setSubmitting(false);
    }
  }, [scope.kind, scope.id, raw, refresh]);

  const handleDelete = useCallback(
    async (itemId: number): Promise<void> => {
      setError(null);
      setLocalDeleting((prev) => new Set(prev).add(itemId));
      try {
        await api.deleteWatchItem(scope.kind, scope.id, itemId);
        await refresh();
      } catch (e) {
        setError(errorText(e, "Couldn't remove the instruction."));
      }
    },
    [scope.kind, scope.id, refresh],
  );

  const items = data?.items ?? [];

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.kicker}>Watch instructions</span>
        <Button
          size="sm"
          onClick={() => void handleAdd()}
          disabled={submitting || raw.trim().length === 0}
        >
          {submitting ? "Adding…" : "Add"}
        </Button>
      </div>

      <p className={styles.help}>
        Tell the AI what to watch for, one instruction at a time. Each one is reviewed and rewritten
        into safe, focused wording before it takes effect.
      </p>

      <label className={styles.label} htmlFor={inputId}>
        Add a watch instruction
      </label>
      <textarea
        id={inputId}
        className={styles.textarea}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        disabled={submitting}
        rows={3}
      />

      {(items.length > 0 || reviewing) && (
        <div className={styles.result}>
          <span className={styles.resultKicker}>Active watch instructions</span>
          <ol className={styles.itemList}>
            {items.map((item) => {
              const ghost =
                localDeleting.has(item.id) || (data?.pending_delete_ids ?? []).includes(item.id);
              const matched = duplicateOfId === item.id;
              return (
                <li
                  key={item.id}
                  className={[
                    styles.item,
                    ghost ? styles.itemGhost : "",
                    matched ? styles.itemMatched : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className={styles.itemBody}>{item.body}</span>
                  {matched && <span className={styles.matchedNote}>already covered</span>}
                  {!ghost && (
                    <button
                      type="button"
                      className={styles.itemDelete}
                      aria-label={`Remove instruction ${item.position}`}
                      title="Remove this instruction"
                      onClick={() => void handleDelete(item.id)}
                    >
                      🗑
                    </button>
                  )}
                </li>
              );
            })}
            {reviewing && <li className={styles.itemPending}>Reviewing…</li>}
          </ol>
        </div>
      )}

      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
    </div>
  );
};
